import {
  type Balance,
  CHECK_IN_THE_MAIL_DAYS,
  PAYMENT_SLOT_LABEL,
  cents,
  checkExpectedUntil,
  checkStillExpected,
  FINAL_SLOT,
  formatMoney,
  paymentMethodText,
  mayMarkPlanPaymentBanked,
  mayReceipt,
  RECEIPTED_BY_MERCURY,
  receiptDate,
} from "@roof-hawk/core";

import { t } from "../i18n";
import type { CustomerPaymentRow } from "../lib/payments";
import type { JobPlan, LiveLink } from "../lib/queries";
import { PlanStanding } from "./PlanStanding";

/**
 * How much of this job is still owed, and where the rest went.
 *
 * Jonathon, 2026-09-08: "there needs to be a balance section in the overview
 * and estimate so we can quickly see how much money is left. that remaining
 * balance needs to correspond throughout roofboard so we don't send an email to
 * a customer that says they only x when they actually owe y."
 *
 * One component on both screens, fed by one function — `jobBalance` in
 * packages/core (CLAUDE.md rule 5). The ongoing board's figure, this card's
 * figure and the amount the balance letter asks for are then the same
 * arithmetic over the same facts, which is the whole point of the request: two
 * screens that each did their own subtraction is how a customer gets told they
 * owe half of what they owe.
 *
 * ## Why the schedule is on it and not just the number
 *
 * "$9,000 left" invites the next question — left of what, and what came in
 * already. So every payment on the schedule is listed with whether its money
 * has arrived, which also makes the total checkable at a glance rather than
 * taken on trust.
 *
 * **Paid means the money is in the bank**, not that somebody ticked a box (see
 * the note at the top of balance.ts). Through Mercury, or handed over in person
 * and recorded here: cash counts at once, a check once Mercury shows it
 * deposited (ADR 0125). Until then a check reads "Check clearing".
 *
 * ## Money on its way
 *
 * A card payment sits at Stripe for two or three days, so from ADR 0139 the
 * balance stops counting it the moment Mercury says `Paid`. The letter has
 * always named it — asking for the rest and saying the remainder is coming —
 * and this card did not, so Josh found the letter asking Cat Edwards for
 * $6,630 while her job screen read $13,260 owed (2026-09-14). Jonathon, asked
 * which is right: show it as on the way, everywhere.
 *
 * So the heading is what there is left to ask for, and what is travelling is
 * named under it. Not folded into "paid": if the card fails, the money goes
 * back onto the bill, and a screen that had called it collected would have no
 * way to explain itself.
 *
 * ## A check in the mail
 *
 * The payment reminders stop when the money is recorded, and a customer who
 * has mailed a check is reminded twice more before it arrives (2026-10-04).
 * **Customer is paying by check** says so on the job: every reminder on it
 * waits `CHECK_IN_THE_MAIL_DAYS`, the card says until when, and recording the
 * check ends it. A fortnight with nothing recorded and the card says the
 * reminders are going again, with the offer to wait another one (ADR 0263).
 *
 * ## Reminders off
 *
 * **Turn off payment reminders** stops every automatic reminder on the job
 * until somebody turns them back on, with no fortnight and no payment needed
 * (ADR 0302). The card says so for as long as it is off.
 */
export function BalanceCard({
  balance,
  title = t("Balance"),
  payments = [],
  stillLive = [],
  checkExpectedAt = null,
  remindersOffAt,
  now = new Date(),
  onRecordPayment,
  onResendReceipt,
  onPaymentLink,
  onCheckComing,
  onRemindersOff,
  plan,
  onPlanPaymentBanked,
}: {
  balance: Balance;
  /** The job's in-house payment plan, where it has one (ADR 0286). */
  plan?: JobPlan | null;
  /** "Balance" on the Overview; the Estimate names the estimate it is under. */
  title?: string;
  /** Checks and cash recorded on this job, with their receipts. */
  payments?: readonly CustomerPaymentRow[];
  /**
   * Earlier payment links still live beside the final request. Raising the
   * final request cancels them at Mercury (ADR 0139); one here is one Mercury
   * refused, or that was never asked, and the customer could pay it twice.
   */
  stillLive?: readonly LiveLink[];
  onRecordPayment?: () => void;
  onResendReceipt?: (paymentId: string) => void;
  /** Ask for an amount that is not one of the scheduled payments (ADR 0141). */
  onPaymentLink?: () => void;
  /** When a rep said the customer is paying by check (ADR 0263). */
  checkExpectedAt?: string | null;
  /** The clock, so a test can set it. */
  now?: Date;
  /** The rep saying a check is coming (true), or that it is not (false). */
  onCheckComing?: (coming: boolean) => void;
  /** When a person turned this job's payment reminders off (ADR 0302). */
  remindersOffAt?: string | null;
  /** A person turning the reminders off (true), or back on (false). */
  onRemindersOff?: (off: boolean) => void;
  /**
   * A person saying a plan payment is in the bank (ADR 0286). It names no
   * slot, so no box settles it.
   */
  onPlanPaymentBanked?: (payment: CustomerPaymentRow) => void;
}) {
  // Nothing sold yet. A card reading "$0.00 owed" on a job with no price is a
  // card that means nothing, and it would sit on every unsold job in the
  // system.
  if (balance.contract <= 0) return null;

  const clearing = new Set(
    payments
      .filter((payment) => payment.state === "submitted")
      .flatMap((payment) => payment.slots),
  );

  return (
    <div className="block bal">
      <div className="bal-head">
        <h3>{title}</h3>
        <span className={balance.owing ? "bal-owed" : "bal-clear"}>
          {balance.owing
            ? t("{amount} left", { amount: balance.toCollectFormatted })
            : t("Paid up")}
        </span>
      </div>

      {balance.onItsWay > 0 && (
        <div className="bal-way">
          {t(
            "{way} on its way — paid, not yet in the bank. The whole balance is {all} until it lands.",
            {
              way: balance.onItsWayFormatted,
              all: balance.outstandingFormatted,
            },
          )}
        </div>
      )}

      {stillLive.map((link) => (
        <div key={link.id} className="bal-live" role="alert">
          {t(
            link.amountCents != null
              ? "{slot} link for {amount}, invoice {invoice}."
              : "{slot} link, invoice {invoice}.",
            {
              slot: t(PAYMENT_SLOT_LABEL[link.slot] ?? "Earlier payment"),
              amount: formatMoney(cents(link.amountCents ?? 0)),
              invoice: link.invoiceId ?? t("unknown"),
            },
          )}{" "}
          {link.error ??
            t(
              "Still live at Mercury, and the final payment request bills it again. The customer could pay it twice. It has not been cancelled yet; void it in Mercury by hand.",
            )}
        </div>
      ))}

      <PlanStanding plan={plan} balance={balance} now={now} />

      <table className="grid bal-rows">
        <tbody>
          {balance.lines.map((line) => (
            <tr key={line.slot} className={line.paid ? "bal-in" : ""}>
              <td>
                {plan && line.slot === FINAL_SLOT
                  ? t("Payment plan")
                  : t(line.label)}
              </td>
              <td className="bal-amt">{line.amountFormatted}</td>
              <td className="bal-state">
                {line.paid
                  ? t("In the bank")
                  : clearing.has(line.slot)
                    ? t("Check clearing")
                    : t("Not yet")}
              </td>
            </tr>
          ))}
          <tr className="bal-total">
            <td>{t("Contract")}</td>
            <td className="bal-amt">{balance.contractFormatted}</td>
            <td className="bal-state">
              {balance.collected > 0
                ? t("{amount} received", { amount: balance.collectedFormatted })
                : t("Nothing received")}
            </td>
          </tr>
        </tbody>
      </table>

      {payments.length > 0 && (
        <table className="grid bal-rows bal-handed">
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id}>
                <td>
                  {t(paymentMethodText(payment.method, payment.check_number))}
                  {payment.toward_plan ? t(", payment plan") : ""}
                  <div className="na-hint">
                    {receiptDate(payment.received_on)}
                  </div>
                </td>
                <td className="bal-amt">
                  {formatMoney(cents(payment.amount_cents))}
                </td>
                <td className="bal-state">
                  {/* A link's money said in the bank: Mercury receipted
                      it, so ours is never offered (ADR 0312). */}
                  {receiptState(payment)}
                  {onResendReceipt &&
                    mayReceipt(payment) &&
                    !payment.receipt_sent_to &&
                    !payment.receipt_sent_at && (
                      <button
                        type="button"
                        className="btn-small"
                        onClick={() => onResendReceipt(payment.id)}
                      >
                        {t("Send receipt")}
                      </button>
                    )}
                  {onPlanPaymentBanked && mayMarkPlanPaymentBanked(payment) && (
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => onPlanPaymentBanked(payment)}
                    >
                      {t("Mark in the bank")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ReminderSwitches
        owing={balance.owing}
        now={now}
        checkExpectedAt={checkExpectedAt}
        remindersOffAt={remindersOffAt ?? null}
        onCheckComing={onCheckComing}
        onRemindersOff={onRemindersOff}
      />

      {onRecordPayment && (
        <button type="button" className="btn-small" onClick={onRecordPayment}>
          {t("Record check or cash")}
        </button>
      )}
      {/* A change order, half a balance somebody wants to split, a repair
          billed after the fact — money the schedule knows nothing about
          (ADR 0141). */}
      {onPaymentLink && (
        <button type="button" className="btn-small" onClick={onPaymentLink}>
          {t("Payment link for another amount")}
        </button>
      )}

      <div className="na-hint">
        {t(
          "Received means the money is in the bank: through Mercury, as cash recorded here, or as a check once Mercury shows it deposited.",
        )}
      </div>
    </div>
  );
}

/** The two ways a person holds the payment reminders on this job. */
function ReminderSwitches({
  owing,
  now,
  checkExpectedAt,
  remindersOffAt,
  onCheckComing,
  onRemindersOff,
}: {
  owing: boolean;
  now: Date;
  checkExpectedAt: string | null;
  remindersOffAt: string | null;
  onCheckComing: ((coming: boolean) => void) | undefined;
  onRemindersOff: ((off: boolean) => void) | undefined;
}) {
  return (
    <>
      {onCheckComing && (
        <PayingByCheck
          expectedAt={checkExpectedAt}
          owing={owing}
          now={now}
          onCheckComing={onCheckComing}
        />
      )}
      {onRemindersOff && (
        <RemindersOff
          offAt={remindersOffAt}
          owing={owing}
          onRemindersOff={onRemindersOff}
        />
      )}
    </>
  );
}

/**
 * The check in the mail, in whichever state it is in (ADR 0263).
 *
 * Nothing said: the button, on a job that is still owed something — a card
 * that is paid up has no reminders to pause. Said: what the card says while
 * the check is expected, and after the wait runs out. Both are said in full,
 * because the one thing a rep must not have to guess is whether the customer
 * is being written to right now.
 */
function PayingByCheck({
  expectedAt,
  owing,
  now,
  onCheckComing,
}: {
  expectedAt: string | null;
  owing: boolean;
  now: Date;
  onCheckComing: (coming: boolean) => void;
}) {
  if (!expectedAt) {
    return owing ? (
      <button
        type="button"
        className="btn-small"
        onClick={() => onCheckComing(true)}
      >
        {t("Customer is paying by check")}
      </button>
    ) : null;
  }
  const until = checkExpectedUntil(expectedAt);
  const waiting = checkStillExpected(expectedAt, now);
  const waitDays = CHECK_IN_THE_MAIL_DAYS;
  const untilDay = String(until ? receiptDate(until.toISOString()) : null);
  return (
    <div className="bal-way" role="status">
      {t(
        waiting
          ? "Paying by check. Payment reminders are paused until {date}; recording the check ends the wait, and if nothing has arrived by then they start again."
          : "A check was expected by {date} and none has been recorded, so the payment reminders are going again.",
        { date: untilDay },
      )}{" "}
      {!waiting && (
        <button
          type="button"
          className="btn-small"
          onClick={() => onCheckComing(true)}
        >
          {t("Still coming — wait another {n} days", { n: waitDays })}
        </button>
      )}
      <button
        type="button"
        className="btn-small"
        onClick={() => onCheckComing(false)}
      >
        {t("Not paying by check")}
      </button>
    </div>
  );
}

/**
 * The job's payment reminders turned off, or the button that does it
 * (ADR 0302).
 *
 * On: the button, on a job still owed something, as with the check. Off: said
 * whatever the balance, with the way back on, so a job that is paid up never
 * hides a switch somebody left off.
 */
function RemindersOff({
  offAt,
  owing,
  onRemindersOff,
}: {
  offAt: string | null;
  owing: boolean;
  onRemindersOff: (off: boolean) => void;
}) {
  if (!offAt) {
    return owing ? (
      <button
        type="button"
        className="btn-small"
        onClick={() => onRemindersOff(true)}
      >
        {t("Turn off payment reminders")}
      </button>
    ) : null;
  }
  return (
    <div className="bal-way" role="status">
      {t(
        "Payment reminders are off for this job since {date}. The customer is not emailed about a payment until they are turned back on.",
        { date: receiptDate(offAt) },
      )}{" "}
      <button
        type="button"
        className="btn-small"
        onClick={() => onRemindersOff(false)}
      >
        {t("Turn payment reminders back on")}
      </button>
    </div>
  );
}

/** What became of a recorded payment's receipt. */
function receiptState(payment: CustomerPaymentRow): string {
  if (!mayReceipt(payment)) return t(RECEIPTED_BY_MERCURY);
  const { receipt_number: n, receipt_error: error } = payment;
  if (payment.receipt_sent_to) return t("Receipt {n} emailed", { n: `${n}` });
  if (error) return t("Receipt not emailed: {error}", { error });
  return t(payment.receipt_sent_at ? "Sending receipt…" : "No receipt yet");
}
